/** Builds: create, attach, cancel, priority, runtime events and the client execution. */
import { z } from 'zod';
import { BuildPriority, ExecutionMode, Platform, uuid } from './common.js';
import { EnvOverride } from './env-vars.js';
import { CiContext, GitRef } from './git.js';
import { easProfileName } from './providers.js';

export const AutoSubmit = z.object({
  enabled: z.boolean(),
  track: z.string().max(64).optional(),
  storeAppId: uuid.optional(),
  rolloutFraction: z.number().gt(0).max(1).optional(),
});

export const BuildCreateRequest = z.object({
  app: z.string().min(1).max(128),
  project: z.string().max(128).optional(),
  platforms: z.union([z.array(Platform).min(1), z.literal('all')]),
  profile: z.string().max(64),
  environment: z.string().max(64),
  git: GitRef,
  inputs: z.record(z.string(), z.unknown()).default({}),
  autoSubmit: AutoSubmit.optional(),
  envOverrides: z.array(EnvOverride).max(50).optional(),
  priority: BuildPriority.default('normal'),
  execution: z.object({ mode: ExecutionMode.optional(), fallbackToRunner: z.boolean().default(true) }).default({ fallbackToRunner: true }),
  ciContext: CiContext.optional(),
  message: z.string().max(500).optional(),
  dryRun: z.boolean().default(false),
});
export type BuildCreateRequest = z.infer<typeof BuildCreateRequest>;

/** An `http(s)` link, at most 500 characters: what Hangar stores as a provider link and the console renders as one. */
const webLink = z.url({ protocol: /^https?$/, error: 'an http(s) URL' }).max(500);

/**
 * `BuildAttachRequest.providerMeta`: the only provider details a client may add to a build when it attaches. Strict on
 * purpose: everything else in `builds.providerMeta` is Hangar's own state (the frozen EAS profile and Bitrise app, the
 * reported `artifactUrl` the verifier downloads, claims and counters), and a client that could write it could make the
 * worker fetch any URL (SSRF) or run another profile.
 */
export const BuildAttachProviderMeta = z.object({
  /** The provider's page for the build (EAS `buildDetailsPageUrl`). */
  buildDetailsPageUrl: webLink.optional(),
  /** Position in the provider's queue at attach time. */
  queuePosition: z.number().int().min(0).max(1_000_000).optional(),
  /** The provider's estimate of the wait before the build starts. */
  estimatedWaitTimeLeftSeconds: z.number().int().min(0).max(86_400 * 7).optional(),
}).strict();
export type BuildAttachProviderMeta = z.infer<typeof BuildAttachProviderMeta>;

export const BuildAttachRequest = z.object({
  providerBuildId: z.string().min(1).max(128),
  providerBuildUrl: webLink,
  providerStatus: z.string().max(64).optional(),
  /** Extra provider details recorded on the build; never secrets. */
  providerMeta: BuildAttachProviderMeta.optional(),
});
export type BuildAttachRequest = z.infer<typeof BuildAttachRequest>;

export const BuildCancelRequest = z.object({ reason: z.string().max(500).optional() });

export const RuntimeEventRequest = z.object({
  phase: z.string().regex(/^[a-z0-9_]{1,64}$/),
  status: z.enum(['started', 'passed', 'failed', 'warning', 'skipped', 'error']),
  message: z.string().max(2000).optional(),
  occurredAt: z.string().datetime().optional(),
  logOffset: z.number().int().min(0).optional(),
  details: z.record(z.string(), z.unknown()).optional(),
});
export type RuntimeEventRequest = z.infer<typeof RuntimeEventRequest>;

/** `ClientExecution` (api/openapi.yaml): what the SDK runs in ci_client mode. */
export const ClientExecution = z.object({
  runtimeToken: z.string(),
  runtimeTokenExpiresAt: z.string(),
  attachDeadline: z.string(),
  provider: z.literal('eas'),
  easProfile: easProfileName.optional(),
  workingDirectory: z.string(),
  versionName: z.string().nullable(),
  buildNumber: z.number().int().nullable(),
  processEnv: z.record(z.string(), z.string()),
  /** The provider command as an argv (`args[0]` is the program, `eas`): the SDK runs it without a shell. */
  args: z.array(z.string()).min(2),
  /** `args` rendered as one POSIX shell line, for display and for SDKs older than `args`. */
  command: z.string(),
  attachUrl: z.string().optional(),
  /**
   * This API's public base URL (`/v1` included): what the provider's build calls back (`HANGAR_API_URL` in the build).
   * It can differ from the URL the CLI used (a local API behind a tunnel), so the CLI injects this one.
   */
  apiUrl: z.string().optional(),
});
export type ClientExecution = z.infer<typeof ClientExecution>;

/** `BuildPriorityRequest`. */
export const BuildPriorityRequest = z.object({ priority: BuildPriority, reason: z.string().max(500).optional() });
export type BuildPriorityRequest = z.infer<typeof BuildPriorityRequest>;
